import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
    withStyles, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, Button, TextField, IconButton
} from "@material-ui/core";
import t from "../../languages/pl";
import PasswordInput from '../Common/InputFields/PasswordInput';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { isPassword, isNullOrEmpty } from './../../utils/helpers';

const useStyles = theme => ({
    dialogActions: {
        display: 'flex',
        justifyContent: "flex-end",
        alignItems: "center"
    },
    backdrop: {
        background: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(1px)"
    },
    container: {
        display: 'flex',
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    typography: {
        lineHeight: 1.5,
        fontSize: '0.8rem',
        textAlign: 'justify',
        padding: theme.spacing(2)
    },
    textfield: {
        minWidth: 250,
        margin: theme.spacing(0.5)
    },
    error: {
        fontSize: "1.2rem",
        marginTop: theme.spacing(1),
        color: "red",
    },
});

const Adornment = ({ state, onClick }) => (
    <IconButton
        size="small"
        onClick={onClick}
    >
        {state ? <VisibilityOff /> : <Visibility />}
    </IconButton>
);

Adornment.propTypes = {
    state: PropTypes.bool,
    onClick: PropTypes.func
};

const ForcePasswordChangeDialog = props => {
    const { classes, open, onClose, repeatedPasswoord, forcePasswordChangeMessage } = props;
    const [newPassword, setNewPassword] = useState('');
    const [confirmedPassword, setConfirmedPassword] = useState('');
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [confirmedPasswordVisible, setConfirmedPasswordVisible] = useState(false);
    const [showPassRequirements, setShowPassRequirements] = useState(false);
    const [passwordsDoesntMatch, setPasswordDoesntMatch] = useState(false);
    const [disabledButton, setDisabledButton] = useState(true);

    useEffect(() => {
        if (newPassword !== confirmedPassword){
            setDisabledButton(true);
        }
        if (newPassword.length > 0 && confirmedPassword.length > 0 && isPassword(newPassword) && isPassword(confirmedPassword) && newPassword === confirmedPassword){
            setPasswordDoesntMatch(false);
            setShowPassRequirements(false);
            setDisabledButton(false);
        } else {
            setDisabledButton(true);
        }
    }, [newPassword, confirmedPassword, showPassRequirements]);

    useEffect(() => {
        if(confirmedPassword === ""){
            setPasswordDoesntMatch(false);
        }
    }, [confirmedPassword]);

    const handleBlur = (e) => {
        if (!isPassword(e.target.value) && !isNullOrEmpty(e.target.value)) {
            setShowPassRequirements(true);
        } else {
            setShowPassRequirements(false);
        }
        if (!showPassRequirements && newPassword !== confirmedPassword) {
            setPasswordDoesntMatch(true);
        } else {
            setPasswordDoesntMatch(false);
        }
        if (confirmedPassword === "") {
            setPasswordDoesntMatch(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            style={{ zIndex: 2000 }}
            BackdropProps={{
                classes: { root: classes.backdrop }
            }}
        >
            <DialogTitle>
                {t.changePassword}
            </DialogTitle>
            <DialogContent>
                    <div className={classes.container}>
                        <TextField
                            label={t.newPassword}
                            type={newPasswordVisible ? "text" : "password"}
                            onChange={(e) => setNewPassword(e.target.value)}
                            onBlur={handleBlur}
                            className={classes.textfield}
                            value={newPassword}
                            InputProps={{
                                endAdornment: <Adornment state={newPasswordVisible} onClick={() => setNewPasswordVisible(!newPasswordVisible)} />
                            }}
                        />
                        <TextField
                            label={t.confirmNewPassword}
                            type={confirmedPasswordVisible ? "text" : "password"}
                            onChange={(e) => setConfirmedPassword(e.target.value)}
                            onBlur={handleBlur}
                            className={classes.textfield}
                            value={confirmedPassword}
                            InputProps={{
                                endAdornment: <Adornment state={confirmedPasswordVisible} onClick={() => setConfirmedPasswordVisible(!confirmedPasswordVisible)} />
                            }}
                        />
                    </div>
                <div className={classes.title}>
                    <Typography
                        variant="body1"
                        className={classes.error}
                        style={{ display: showPassRequirements ? "block" : "none" }}
                    >
                        {t.passwordRequiredParameters}
                    </Typography>

                    <Typography
                        variant="body1"
                        className={classes.error}
                        style={{ display: (!showPassRequirements && passwordsDoesntMatch) ? "block" : "none" }}
                    >
                        {t.passwordsDoesntMatch}
                    </Typography>

                    <Typography
                        variant="body1"
                        className={classes.error}
                        style={{ display: (repeatedPasswoord) ? "block" : "none" }}
                    >
                        {forcePasswordChangeMessage}
                    </Typography>
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={disabledButton}
                    onClick={() => onClose(newPassword)}
                >
                    {t.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ForcePasswordChangeDialog.propTypes = {
    classes: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    repeatedPasswoord: PropTypes.bool,
    forcePasswordChangeMessage: PropTypes.string,
};

export default withStyles(useStyles)(ForcePasswordChangeDialog);
